'use client'

import clsx from 'clsx'
import type React from 'react'
import { createContext, useContext, useState } from 'react'
import { Link } from './Link'

const TableContext = createContext<{ bleed: boolean; dense: boolean; grid: boolean; striped: boolean }>({
    bleed: false,
    dense: false,
    grid: false,
    striped: false,
})

export function Table({
    bleed = false,
    dense = false,
    grid = false,
    striped = false,
    className,
    children,
    ...props
}: { bleed?: boolean; dense?: boolean; grid?: boolean; striped?: boolean } & React.ComponentPropsWithoutRef<'div'>) {
    // Contained horizontal scroll: tables now always sit inside a padded Card, so the scroll area
    // is clipped to the table's own box rather than bleeding past the card's padding/rounded edge.
    return (
        <TableContext.Provider value={{ bleed, dense, grid, striped } as React.ContextType<typeof TableContext>}>
            <div {...props} className={clsx(className, 'overflow-x-auto rounded-xl')}>
                <table className="w-full min-w-max whitespace-nowrap text-left text-sm/6 text-zinc-950 dark:text-white">{children}</table>
            </div>
        </TableContext.Provider>
    )
}

export function TableHead({ className, ...props }: React.ComponentPropsWithoutRef<'thead'>) {
    return <thead {...props} className={clsx(className, 'text-zinc-500 dark:text-zinc-400')} />
}

export function TableBody(props: React.ComponentPropsWithoutRef<'tbody'>) {
    return <tbody {...props} />
}

const TableRowContext = createContext<{ href?: string; target?: string; title?: string }>({
    href: undefined,
    target: undefined,
    title: undefined,
})

export function TableRow({
    href,
    target,
    title,
    className,
    ...props
}: { href?: string; target?: string; title?: string } & React.ComponentPropsWithoutRef<'tr'>) {
    let { striped } = useContext(TableContext)

    return (
        <TableRowContext.Provider value={{ href, target, title } as React.ContextType<typeof TableRowContext>}>
            <tr
                {...props}
                className={clsx(
                    className,
                    href &&
                    'has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-ring',
                    striped && 'even:bg-secondary/60',
                    'hover:bg-secondary'
                )}
            />
        </TableRowContext.Provider>
    )
}

export function TableHeader({ className, ...props }: React.ComponentPropsWithoutRef<'th'>) {
    let { bleed } = useContext(TableContext)

    return (
        <th
            {...props}
            className={clsx(
                className,
                'bg-secondary px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground first:rounded-l-lg first:pl-[var(--gutter,theme(spacing.2))] last:rounded-r-lg last:pr-[var(--gutter,theme(spacing.2))]',
                !bleed && 'sm:first:pl-1 sm:last:pr-1'
            )}
        />
    )
}

export function TableCell({ className, children, ...props }: React.ComponentPropsWithoutRef<'td'>) {
    let { bleed, dense } = useContext(TableContext)
    let { href, target, title } = useContext(TableRowContext)
    let [cellRef, setCellRef] = useState<HTMLElement | null>(null)

    return (
        <td
            ref={href ? setCellRef : undefined}
            {...props}
            className={clsx(
                className,
                'relative px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))]',
                dense ? 'py-2.5' : 'py-4',
                !bleed && 'sm:first:pl-1 sm:last:pr-1'
            )}
        >
            {href && (
                <Link
                    data-row-link
                    href={href}
                    target={target}
                    aria-label={title}
                    tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
                    className="absolute inset-0 focus:outline-none"
                />
            )}
            {children}
        </td>
    )
}
